import {
  convertServerResponsivePropertyToClientResponsiveProperty,
  ISDUIResponsiveValue,
} from "@faire/retailer-visitor-shared/components/SDUI/__internal__/Styling/responsiveValues";
import { SDUILayoutRendererItem } from "@faire/retailer-visitor-shared/components/SDUI/LayoutRenderers/types";
import {
  assignInlineVars,
  DynamicVariable,
  mapResponsivePropToCSSVars,
} from "@faire/retailer-visitor-shared/lib/tailwindUtils";
import { cn } from "@faire/web--source/tools/cn";
import React from "react";

import { SDUIFlowLayoutItemOnVisibilityRenderer } from "./SDUIFlowLayoutItemOnVisibilityRenderer";
import { SDUIFlowLayoutItemWithUPLTRef } from "./SDUIFlowLayoutItemWithUPLTRef";

const colSpanMobileVar: DynamicVariable<"--f-grid-col"> = {
  variable: "--f-grid-col",
  className: "col-(--f-grid-col)",
};

const colSpanTabletVar: DynamicVariable<"--f-grid-col-md"> = {
  variable: "--f-grid-col-md",
  className: "md:col-(--f-grid-col-md)",
};

const colSpanDesktopVar: DynamicVariable<"--f-grid-col-lg"> = {
  variable: "--f-grid-col-lg",
  className: "lg:col-(--f-grid-col-lg)",
};

const colSpanXLargeVar: DynamicVariable<"--f-grid-col-xl"> = {
  variable: "--f-grid-col-xl",
  className: "xl:col-(--f-grid-col-xl)",
};

const orderMobileVar: DynamicVariable<"--f-order"> = {
  variable: "--f-order",
  className: "order-(--f-order)",
};

const orderTabletVar: DynamicVariable<"--f-order-md"> = {
  variable: "--f-order-md",
  className: "md:order-(--f-order-md)",
};

const orderDesktopVar: DynamicVariable<"--f-order-lg"> = {
  variable: "--f-order-lg",
  className: "lg:order-(--f-order-lg)",
};

const orderXLargeVar: DynamicVariable<"--f-order-xl"> = {
  variable: "--f-order-xl",
  className: "xl:order-(--f-order-xl)",
};

export const SDUIFlowLayoutItem = ({
  itemColSpans,
  item: { item, itemKey },
  itemOrder,
  shouldRenderOnVisibility,
  visibilityFlagIndex,
  shouldUpdateFlagOnVisibility,
  shouldAttachUPLTRef,
  pageLoadKey,
  enableLoadingPlaceholder,
  rowSpacingClasses,
}: {
  itemColSpans: ISDUIResponsiveValue<number>;
  item: SDUILayoutRendererItem;
  itemOrder: ISDUIResponsiveValue<number> | undefined;
  shouldRenderOnVisibility?: boolean;
  visibilityFlagIndex?: number;
  shouldUpdateFlagOnVisibility?: boolean;
  shouldAttachUPLTRef?: boolean;
  pageLoadKey?: string;
  enableLoadingPlaceholder?: boolean;
  rowSpacingClasses?: string[];
}) => {
  const colSpansClientProperty =
    convertServerResponsivePropertyToClientResponsiveProperty(
      itemColSpans.mobile_and_above ?? 0,
      (value) => `span ${value}`,
      itemColSpans
    );

  const { vars: colSpanVars, classes: colSpanClasses } =
    mapResponsivePropToCSSVars(colSpansClientProperty, {
      mobileAndAbove: colSpanMobileVar,
      tabletAndAbove: colSpanTabletVar,
      desktopAndAbove: colSpanDesktopVar,
      xLargeAndAbove: colSpanXLargeVar,
    });

  let orderVars: Record<string, string | undefined> | undefined = undefined;
  let orderClasses: string[] | undefined = undefined;
  if (itemOrder) {
    const orderProperty =
      convertServerResponsivePropertyToClientResponsiveProperty(
        itemOrder.mobile_and_above ?? 0,
        (value) => `${value}`,
        itemOrder
      );

    const { vars, classes } = mapResponsivePropToCSSVars(orderProperty, {
      mobileAndAbove: orderMobileVar,
      tabletAndAbove: orderTabletVar,
      desktopAndAbove: orderDesktopVar,
      xLargeAndAbove: orderXLargeVar,
    });
    orderVars = vars;
    orderClasses = classes;
  }

  let renderedItem = item;
  if (shouldRenderOnVisibility) {
    renderedItem = (
      <SDUIFlowLayoutItemOnVisibilityRenderer
        flagIndex={visibilityFlagIndex ?? 0}
        shouldUpdateFlagOnVisibility={shouldUpdateFlagOnVisibility}
        enableLoadingPlaceholder={enableLoadingPlaceholder}
        component={item}
      />
    );
  } else if (shouldAttachUPLTRef) {
    renderedItem = (
      <SDUIFlowLayoutItemWithUPLTRef pageLoadKey={pageLoadKey ?? ""}>
        {item}
      </SDUIFlowLayoutItemWithUPLTRef>
    );
  }
  return (
    <div
      className={cn(
        "h-full",
        colSpanClasses,
        itemColSpans.mobile_and_above ? "inline-block" : "hidden",
        itemColSpans.tablet_and_above ? "md:inline-block" : "md:hidden",
        itemColSpans.desktop_and_above ? "lg:inline-block" : "lg:hidden",
        itemColSpans.x_large_and_above ? "xl:inline-block" : "xl:hidden",
        orderClasses,
        rowSpacingClasses
      )}
      key={itemKey}
      style={assignInlineVars({
        ...colSpanVars,
        ...orderVars,
      })}
    >
      {renderedItem}
    </div>
  );
};
